import { AnalyticsProvider } from '@cmpsr/analytics';
import { ComposerProvider } from '@cmpsr/components';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthKitProvider } from '@workos-inc/authkit-react';
import { WorkOsWidgets } from '@workos-inc/widgets';
import '@radix-ui/themes/styles.css';
import '@workos-inc/widgets/styles.css';
import './styles/workos-widgets-overrides.css';
import { theme } from '@/constants/theme';
import { type ReactNode } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { queryClient } from './client/query';
import { ImpersonationBanner } from './components/impersonation-banner/impersonation-banner';
import { WorkosAuthBridge } from './components/workos-auth-bridge';
import { AmplitudeProvider } from './context/me/amplitude.context';
import router from './router';
import { FeatureFlagProvider } from './services/feature-flag-provider';
import injectThemeProps from './services/theme-override.service';

const workosClientId = import.meta.env.VITE_WORKOS_CLIENT_ID;

function MaybeAuthKitProvider({ children }: { children: ReactNode }) {
  if (!workosClientId) {
    return <>{children}</>;
  }

  // Note: AuthKit's default onRedirectCallback handling runs in-tree once the
  // OAuth callback completes. WorkosAuthBridge then registers the access-token
  // function and sets the session flag before children render — and the
  // sign-in page's WorkosSignInRedirect navigates to the dashboard once `user`
  // is populated. No manual flag-write or window.location reload needed.
  return (
    <AuthKitProvider clientId={workosClientId}>
      <WorkosAuthBridge>
        <ImpersonationBanner />
        <WorkOsWidgets
          queryClient={queryClient}
          theme={{
            appearance: 'light',
            accentColor: 'indigo',
            grayColor: 'sand',
            radius: 'medium',
            fontFamily: 'Inter, sans-serif',
            hasBackground: false,
            panelBackground: 'solid',
          }}
        >
          {children}
        </WorkOsWidgets>
      </WorkosAuthBridge>
    </AuthKitProvider>
  );
}

export function App() {
  return (
    <ComposerProvider theme={injectThemeProps(theme)}>
      <QueryClientProvider client={queryClient}>
        {/* Are we getting anything from the AnalyticsProvider anymore? */}
        <AnalyticsProvider>
          <AmplitudeProvider>
            <FeatureFlagProvider>
              <Sentry.ErrorBoundary
                fallback={({ error, resetError }) => (
                  <div className="flex flex-col items-center justify-center min-h-screen p-4">
                    <h1 className="text-2xl font-semibold mb-4">Something went wrong</h1>
                    <p className="text-gray-600 mb-4">
                      {error instanceof Error ? error.message : 'An unexpected error occurred'}
                    </p>
                    <button
                      onClick={resetError}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Try again
                    </button>
                  </div>
                )}
                showDialog={false}
              >
                <MaybeAuthKitProvider>
                  <RouterProvider router={createBrowserRouter(router)} />
                </MaybeAuthKitProvider>
              </Sentry.ErrorBoundary>
            </FeatureFlagProvider>
          </AmplitudeProvider>
        </AnalyticsProvider>
      </QueryClientProvider>
    </ComposerProvider>
  );
}

export default App;
